import { Spin } from 'antd'
import {
  LABEL_WIDTH,
  SectionContainer,
  Sub,
  TooltipDivider,
} from 'features/indicationTriage/cards/ffsCalc/FfsCalcLayout'
import {
  LABEL_T_C_MIN,
  LABEL_T_L_MIN,
  LABEL_T_LIM,
  LABEL_THICKNESS_CRITERIA,
} from 'features/indicationTriage/cards/ffsCalc/FfsFieldLabels'
import { FfsParameterField } from 'features/indicationTriage/cards/ffsCalc/FfsParameterField'
import { MawpCalcType } from 'features/indicationTriage/cards/ffsCalc/FfsSharedValues'
import { useFfsStrategy } from 'features/indicationTriage/cards/ffsCalc/ffsStrategyContext/FfsStrategyContext'
import {
  formatGteThreshold,
  WORKING_UNIT_THICKNESS,
} from 'features/indicationTriage/cards/ffsCalc/ffsUnits'
import { useGeckoLocale } from 'features/locale'
import isNil from 'lodash/isNil'
import React, { FC, useMemo } from 'react'
import { GeckoMeasurement, GeckoMeasurementUnit } from 'types'

const TooltipContentTLim: FC<{
  criteriaThicknessThreshold: GeckoMeasurement
}> = ({ criteriaThicknessThreshold }) => {
  const { formatMeasurement } = useGeckoLocale()
  return (
    <>
      Limiting thickness
      <TooltipDivider />t<Sub>lim</Sub> = max(0.2 × t<Sub>nom</Sub>,{' '}
      {formatMeasurement({ measurement: criteriaThicknessThreshold })})
    </>
  )
}
export const TOOLTIP_T_C_MIN = (
  <>
    Minimum circumferential thickness
    <TooltipDivider />
    Calculated from design MAWP using circumferential stress formula
  </>
)
export const TOOLTIP_T_L_MIN = (
  <>
    Minimum longitudinal thickness
    <TooltipDivider />
    Calculated from design MAWP using longitudinal stress formula
  </>
)
export const TOOLTIP_THICKNESS_CRITERIA = (
  <>
    Minimum Measured Thickness Criteria
    <TooltipDivider />
    Pass if (t<Sub>mm</Sub> - FCA<Sub>ml</Sub>) ≥ max(0.5×t<Sub>min</Sub>, t
    <Sub>lim</Sub>)
  </>
)
interface EffectiveThicknessProps {
  t_l: GeckoMeasurement | null
  t_c: GeckoMeasurement | null
  tmm: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
}
interface EffectiveThicknessResult {
  tsEffective: GeckoMeasurement | null
  tcEffective: GeckoMeasurement | null
  effectiveMeasuredThickness: GeckoMeasurement | null
}
export function getEffectiveThickness({
  t_l,
  t_c,
  tmm,
  fcaMl,
}: EffectiveThicknessProps): EffectiveThicknessResult {
  if (isNil(fcaMl))
    return {
      tsEffective: null,
      tcEffective: null,
      effectiveMeasuredThickness: null,
    }

  return {
    tsEffective: !isNil(t_l)
      ? GeckoMeasurement.CreateFromValue(
          t_l.convertTo(WORKING_UNIT_THICKNESS).num -
            fcaMl?.convertTo(WORKING_UNIT_THICKNESS).num,
          WORKING_UNIT_THICKNESS
        )
      : null,
    tcEffective: !isNil(t_c)
      ? GeckoMeasurement.CreateFromValue(
          t_c.convertTo(WORKING_UNIT_THICKNESS).num -
            fcaMl.convertTo(WORKING_UNIT_THICKNESS).num,
          WORKING_UNIT_THICKNESS
        )
      : null,
    effectiveMeasuredThickness: !isNil(tmm)
      ? GeckoMeasurement.CreateFromValue(
          tmm.convertTo(WORKING_UNIT_THICKNESS).num -
            fcaMl.convertTo(WORKING_UNIT_THICKNESS).num,
          WORKING_UNIT_THICKNESS
        )
      : null,
  }
}
interface ThicknessCriteriaCheckProps {
  tmm: GeckoMeasurement | null
  fcaMl: GeckoMeasurement | null
  effectiveMeasuredThickness: GeckoMeasurement | null
  outerDiameter: GeckoMeasurement | null
  designPressure: GeckoMeasurement | null
  nominalThickness: GeckoMeasurement | null
  yieldStrength: GeckoMeasurement | null
  qualityFactor: GeckoMeasurement | null
  pressureCoefficient: GeckoMeasurement | null
  weldStrengthReduction?: GeckoMeasurement | null
  tsl: GeckoMeasurement | null
  // Optional: PV-specific parameters
  tNom?: GeckoMeasurement | null
  calcType?: MawpCalcType | null
}
export interface FfsThicknessCriteriaSectionProps {
  tLim: GeckoMeasurement | null
  tCMin: GeckoMeasurement | null
  tLMin: GeckoMeasurement | null
  thicknessCriteriaMin: GeckoMeasurement | null
  effectiveMeasuredThickness: GeckoMeasurement | null
  failingThicknessCriteria: boolean
  loading: boolean
}

export function useThicknessCriteriaCheck({
  effectiveMeasuredThickness,
  outerDiameter,
  designPressure,
  yieldStrength,
  qualityFactor,
  pressureCoefficient,
  weldStrengthReduction,
  tsl,
  nominalThickness,
  tNom,
  calcType,
}: ThicknessCriteriaCheckProps): Omit<
  FfsThicknessCriteriaSectionProps,
  'loading'
> {
  const {
    strategy: {
      mawp: mawpStrategy,
      analysisResult: { criteriaThicknessThreshold },
    },
  } = useFfsStrategy()

  const tLim = useMemo(() => {
    if (!nominalThickness) return null

    const tNomInMm = nominalThickness.convertTo(WORKING_UNIT_THICKNESS).num
    const criteriaThicknessThresholdInMm =
      criteriaThicknessThreshold?.convertTo(
        GeckoMeasurementUnit.Millimeters
      ).num
    const tLimMm = Math.max(0.2 * tNomInMm, criteriaThicknessThresholdInMm)

    return GeckoMeasurement.CreateFromValue(
      tLimMm,
      GeckoMeasurementUnit.Millimeters
    ).convertTo(WORKING_UNIT_THICKNESS)
  }, [nominalThickness, criteriaThicknessThreshold])

  // Calculate minimum circumferential thickness from design MAWP
  const tCMin = useMemo(() => {
    if (isNil(mawpStrategy?.computeThicknessFromMawp)) {
      return null
    }
    const result = mawpStrategy.computeThicknessFromMawp({
      mawp: designPressure,
      yieldStrength,
      qualityFactor,
      outerDiameter,
      pressureCoefficient,
      weldStrengthReduction,
      tNom,
      calcType,
      tsl: null, // Circumferential doesn't use tsl
      axis: 'x',
    })
    return result ? result.convertTo(WORKING_UNIT_THICKNESS) : null
  }, [
    mawpStrategy,
    designPressure,
    yieldStrength,
    qualityFactor,
    outerDiameter,
    pressureCoefficient,
    weldStrengthReduction,
    tNom,
    calcType,
  ])

  // Calculate minimum longitudinal thickness from design MAWP
  const tLMin = useMemo(() => {
    if (isNil(mawpStrategy?.computeThicknessFromMawp)) {
      return null
    }
    const result = mawpStrategy.computeThicknessFromMawp({
      mawp: designPressure,
      yieldStrength,
      qualityFactor,
      outerDiameter,
      pressureCoefficient,
      weldStrengthReduction,
      tNom,
      calcType,
      tsl,
      axis: 'y',
    })
    return result?.convertTo(WORKING_UNIT_THICKNESS) ?? null
  }, [
    mawpStrategy,
    designPressure,
    yieldStrength,
    qualityFactor,
    outerDiameter,
    pressureCoefficient,
    weldStrengthReduction,
    tNom,
    calcType,
    tsl,
  ])

  // Calculate minimum thickness criteria: max(0.5*t_min, t_lim)
  const thicknessCriteriaMin = useMemo(() => {
    if (!tLim) return null

    // Calculate t_min = max(t^c_min, t^L_min)
    const tMin =
      tCMin && tLMin
        ? GeckoMeasurement.CreateFromValue(
            Math.max(tCMin.num, tLMin.num),
            WORKING_UNIT_THICKNESS
          )
        : (tCMin ?? tLMin)

    if (!tMin) return tLim

    const halfTMin = 0.5 * tMin.num
    const tLimValue = tLim.num

    return GeckoMeasurement.CreateFromValue(
      Math.max(halfTMin, tLimValue),
      WORKING_UNIT_THICKNESS
    )
  }, [tCMin, tLMin, tLim])

  return useMemo(() => {
    return {
      tLim,
      tCMin,
      tLMin,
      thicknessCriteriaMin,
      effectiveMeasuredThickness,
      failingThicknessCriteria: Boolean(
        !effectiveMeasuredThickness ||
        !thicknessCriteriaMin ||
        effectiveMeasuredThickness.num < thicknessCriteriaMin.num
      ),
    }
  }, [tLim, tCMin, tLMin, thicknessCriteriaMin, effectiveMeasuredThickness])
}

const RawFfsThicknessCriteriaSection: FC<FfsThicknessCriteriaSectionProps> = ({
  tLim,
  tCMin,
  tLMin,
  thicknessCriteriaMin,
  effectiveMeasuredThickness,
  failingThicknessCriteria,
  loading,
}) => {
  const {
    strategy: {
      analysisResult: { criteriaThicknessThreshold },
    },
  } = useFfsStrategy()

  return (
    <Spin spinning={loading}>
      <SectionContainer>
        <FfsParameterField
          label={LABEL_T_LIM}
          labelTooltip={
            <TooltipContentTLim
              criteriaThicknessThreshold={criteriaThicknessThreshold}
            />
          }
          labelWidth={LABEL_WIDTH}
          value={tLim}
          measurementType='thickness'
        />
        <FfsParameterField
          label={LABEL_T_C_MIN}
          labelTooltip={TOOLTIP_T_C_MIN}
          labelWidth={LABEL_WIDTH}
          value={tCMin}
          measurementType='thickness'
        />
        <FfsParameterField
          label={LABEL_T_L_MIN}
          labelTooltip={TOOLTIP_T_L_MIN}
          labelWidth={LABEL_WIDTH}
          value={tLMin}
          measurementType='thickness'
        />
        <FfsParameterField
          label={LABEL_THICKNESS_CRITERIA}
          labelTooltip={TOOLTIP_THICKNESS_CRITERIA}
          labelWidth={LABEL_WIDTH}
          value={
            effectiveMeasuredThickness?.convertTo(WORKING_UNIT_THICKNESS) ??
            null
          }
          measurementType='thickness'
          isValid={!failingThicknessCriteria}
          trailingText={(value) =>
            formatGteThreshold(thicknessCriteriaMin, {
              unit: value?.unit ?? WORKING_UNIT_THICKNESS,
              fallback: '≥ N/A',
            })
          }
        />
      </SectionContainer>
    </Spin>
  )
}

export const FfsThicknessCriteriaSection = React.memo(
  RawFfsThicknessCriteriaSection
)
